/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { gql } from '@apollo/client'
import { Avatar, CheckIcon, IconButton, Link, Separator, UserAddIcon } from '@farol-ds/react'
import {
  InfoSourceDisclaimer,
  ProfileQuickview,
  useProfileFollow,
} from '@jusbrasil-web/docview/core'
import { useRef } from 'react'

import { Details } from '../details'
import { PublicationSidebar_DocumentFragment } from './__generated__/publication-sidebar.graphql'
import styles from './publication-sidebar.module.scss'

export interface PublicationSidebarProps {
  document: PublicationSidebar_DocumentFragment
}

const TERMS_URL = 'https://www.jusbrasil.com.br/termos-de-uso'
const PUBLISHING_RULES_URL =
  'https://www.jusbrasil.com.br/artigos/regras-de-publicacao-da-comunidade-jusbrasil/111561143'

export function PublicationSidebar(props: PublicationSidebarProps) {
  const { document } = props
  const { publisher } = document

  const quickviewRef = useRef<HTMLDivElement>(null)

  const follow = useProfileFollow({ profile: publisher })

  return (
    <div className={styles.container}>
      {publisher && (
        <>
          <div className={styles.author}>
            <a href={publisher.url!}>
              <Avatar
                src={publisher.avatar?.urlxl || undefined}
                alt={[publisher.fullname, publisher.occupation?.title].filter(Boolean).join(', ')}
                size="xl"
                ref={quickviewRef}
              />
            </a>
            <p className={styles.authorTitle}>
              <Link type="ghost" href={publisher.url!}>
                {publisher.fullname}
              </Link>
            </p>
            <div>
              <IconButton
                icon={follow.isFollowing ? <CheckIcon /> : <UserAddIcon />}
                kind="tertiary"
                label={follow.isFollowing ? 'Seguindo' : 'Seguir'}
                loading={follow.loading}
                onClick={follow.toggle}
              />
            </div>
          </div>
          <ProfileQuickview
            profile={publisher}
            getElementNodes={() => (quickviewRef.current ? [quickviewRef.current] : [])}
          />
        </>
      )}

      <Details document={document} />

      <Separator className={styles.separator} />

      <InfoSourceDisclaimer title="De onde vêm as informações do Jusbrasil?">
        Este conteúdo foi produzido e/ou disponibilizado por pessoas da Comunidade, que são
        responsáveis pelas respectivas opiniões. O Jusbrasil realiza a moderação do conteúdo de
        nossa Comunidade. Mesmo assim, caso entenda que o conteúdo deste artigo viole as Regras de
        Publicação, clique na opção &quot;reportar&quot; que o nosso time irá avaliar o relato e
        tomar as medidas cabíveis, se necessário. Conheça nossos{' '}
        <a href={TERMS_URL}>Termos de uso</a> e{' '}
        <a href={PUBLISHING_RULES_URL}>Regras de Publicação</a>.
      </InfoSourceDisclaimer>
    </div>
  )
}

PublicationSidebar.fragments = {
  document: gql`
    fragment PublicationSidebar_document on Document {
      publisher {
        fullname
        url
        avatar: thumborAvatar {
          urlxl: thumbUrl(size: "48x48")
        }
        occupation {
          title
        }

        ...ProfileQuickview_profile
        ...UseProfileFollow_profile
      }

      ...Details_document
    }

    ${Details.fragments.document}
    ${ProfileQuickview.fragments.profile}
    ${useProfileFollow.fragments.profile}
  `,
}
